import { Injectable } from '@angular/core';
import {
  ApplicationAppEntityApplicationRead,
  ApplicationService,
  LocationDecisionService,
  ParcelService, RoadOccupationAppEntityRoadOccupationRead,
  RoadOccupationService,
  RoadOccupationStageService
} from '../openapi';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import {
  OccupationOfLanePlacementConstructionObjectsFormValue
} from '../containers/applications/application/occupation-of-lane-placement-construction-objects/sections/occupation-of-lane-placement-construction-objects-section/forms/occupation-of-lane-placement-construction-objects.form';
import {
  RoadOccupationFormValue
} from '../containers/applications/application/common/occupation-data-section/forms/road-occupation.form';
import { toNumber } from './helpers';
import {
  AdPlacementRoadOccupationFormValue
} from '../containers/applications/application/placement-advertising/sections/ad-placement-section/forms/ad-placement-road-occupation.form';
import {
  LocationOfDevicesUnrelatedToRoadOccupationFormValue
} from '../containers/applications/application/location-of-devices-unrelated-to-road/sections/location-of-devices-unrelated-to-road-occupation-data-section/forms/location-of-devices-unrelated-to-road-occupation.form';

@Injectable({
  providedIn: 'root'
})
export class RoadOccupationCommonService {
  constructor(
    private applicationService: ApplicationService,
    private roadOccupationService: RoadOccupationService,
    private roadOccupationStageService: RoadOccupationStageService,
    private locationDecisionService: LocationDecisionService,
    private parcelService: ParcelService
  ) {
  }

  public updateOccupations(application: ApplicationAppEntityApplicationRead | null, occupations: RoadOccupationFormValue[]): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = (application?.roadOccupations || []).map((v: any) => this.roadOccupationService.deleteRoadOccupationRoadOccupationItem({ id: v?.uuid || '' }));
    application?.roadOccupations?.forEach(occupation => {
      occupation?.locationDecisions?.forEach(v => {
        deleteRequests.push(this.locationDecisionService.deleteLocationDecisionLocationDecisionItem({
          id: v?.uuid || ''
        }));
      });

      occupation?.stages?.forEach(stage => {
        stage?.parcels?.forEach(parcel => {
          deleteRequests.push(this.parcelService.deleteParcelParcelItem({ id: parcel?.uuid || '' }));
        });
      });
    });

    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() => {
        return forkJoin(
          [...occupations].reverse().map(roadOccupationFormValue => {
            const createDecisionRequests = roadOccupationFormValue?.locationDecisions?.map(locationDecision => this.locationDecisionService.postLocationDecisionLocationDecisionCollection({
              locationDecision
            }).pipe(map(decision => decision.id || '')));
            return (createDecisionRequests?.length ? forkJoin(createDecisionRequests) : of([]))
              .pipe(
                switchMap((decisionIds) => this.roadOccupationService.postRoadOccupationRoadOccupationCollection({
                  roadOccupation: {
                    application: application?.uuid,
                    locationDecisionNumber: roadOccupationFormValue?.locationDecisionNumber,
                    locationDecisionDate: roadOccupationFormValue?.locationDecisionDate || undefined,
                    agreementNumber: roadOccupationFormValue?.agreementNumber || undefined,
                    agreementDate: roadOccupationFormValue?.agreementDate || undefined,
                    purpose: roadOccupationFormValue?.purpose,
                    city: roadOccupationFormValue?.city,
                    occupationType: roadOccupationFormValue?.occupationType,
                    typeOfFailure: roadOccupationFormValue?.typeOfFailure,
                    surfaceViolation: roadOccupationFormValue?.surfaceViolation,
                    workPurpose: roadOccupationFormValue?.workPurpose,
                    locationDecisions: decisionIds
                  }
                }).pipe(
                  switchMap((roadOccupation: RoadOccupationAppEntityRoadOccupationRead) => forkJoin(
                      [...roadOccupationFormValue.stages].reverse().map((stage, index) => {

                        const createParcelsRequests = stage.parcels.map(parcel => this.parcelService.postParcelParcelCollection({ parcel }));

                        return (createParcelsRequests.length ? forkJoin(createParcelsRequests) : of([])).pipe(
                          switchMap((parcels) => this.roadOccupationStageService.postRoadOccupationStageRoadOccupationStageCollection({
                              roadOccupationStage: {
                                ordinalNumber: index + 1,
                                startDate: stage?.startDate || undefined,
                                endDate: stage?.endDate || undefined,
                                dayQuantity: stage?.dayQuantity || undefined,

                                infrastructureTelecommunicationOnly: stage?.infrastructureTelecommunicationOnly,

                                geometry: stage?.geometry || undefined,

                                roadName: stage.roadName,
                                roadNumber: stage.roadNumber,
                                street: stage.street,

                                parcels: parcels.map(v => v?.uuid || '') || [],

                                roadWidth: toNumber(stage?.roadWidth) || undefined,
                                roadWidth2: toNumber(stage?.roadWidth2) || undefined,
                                roadWidth3: toNumber(stage?.roadWidth3) || undefined,

                                roadLength: toNumber(stage?.roadLength) || undefined,
                                roadLength2: toNumber(stage?.roadLength2) || undefined,
                                roadLength3: toNumber(stage?.roadLength3) || undefined,

                                roadArea: toNumber(stage?.roadArea) || undefined,
                                roadArea2: toNumber(stage?.roadArea2) || undefined,
                                roadArea3: toNumber(stage?.roadArea3) || undefined,
                                sidewalkArea: toNumber(stage?.sidewalkArea) || undefined,
                                sidewalkWidth: toNumber(stage?.sidewalkWidth) || undefined,
                                sidewalkLength: toNumber(stage?.sidewalkLength) || undefined,
                                otherElementsArea: toNumber(stage?.otherElementsArea) || undefined,
                                otherElementsWidth: toNumber(stage?.otherElementsWidth) || undefined,
                                otherElementsLength: toNumber(stage?.otherElementsLength) || undefined,
                                roadOccupation: roadOccupation?.uuid
                              }
                            })
                          )
                        );
                      })
                    )
                  )
                ))
              );
          }));
      }),
      switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
    );
  }

  public occupationOfLanePlacementConstructionObjects(
    application: ApplicationAppEntityApplicationRead | null,
    occupations: OccupationOfLanePlacementConstructionObjectsFormValue[]
  ): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = (application?.roadOccupations || []).map((v: any) => this.roadOccupationService.deleteRoadOccupationRoadOccupationItem({ id: v?.uuid || '' }));
    application?.roadOccupations?.forEach(occupation => {
      occupation?.locationDecisions?.forEach(v => {
        deleteRequests.push(this.locationDecisionService.deleteLocationDecisionLocationDecisionItem({
          id: v?.uuid || ''
        }));
      });

      occupation?.stages?.forEach(stage => {
        stage?.parcels?.forEach(parcel => {
          deleteRequests.push(this.parcelService.deleteParcelParcelItem({ id: parcel?.uuid || '' }));
        });
      });
    });
    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() => {
        return forkJoin(
          [...occupations].reverse().map(roadOccupationFormValue => {
            const createDecisionRequests = roadOccupationFormValue?.locationDecisions?.map(locationDecision => this.locationDecisionService.postLocationDecisionLocationDecisionCollection({
              locationDecision
            }).pipe(map(decision => decision.id || '')));
            return (createDecisionRequests?.length ? forkJoin(createDecisionRequests) : of([]))
              .pipe(
                switchMap((decisionIds) => this.roadOccupationService.postRoadOccupationRoadOccupationCollection({
                  roadOccupation: {
                    application: application?.uuid,
                    locationDecisions: decisionIds
                  }
                }).pipe(
                  switchMap((roadOccupation: RoadOccupationAppEntityRoadOccupationRead) => forkJoin(
                      [...roadOccupationFormValue.stages].reverse().map((stage, index) => {

                        const createParcelsRequests = stage.parcels.map(parcel => this.parcelService.postParcelParcelCollection({ parcel }));

                        return (createParcelsRequests.length ? forkJoin(createParcelsRequests) : of([])).pipe(
                          switchMap((parcels) => this.roadOccupationStageService.postRoadOccupationStageRoadOccupationStageCollection({
                              roadOccupationStage: {
                                ordinalNumber: index + 1,
                                roadName: stage.roadName,
                                roadNumber: stage.roadNumber,
                                street: stage.street,

                                startDate: stage?.startDate || undefined,
                                endDate: stage?.endDate || undefined,
                                dayQuantity: stage?.dayQuantity || undefined,
                                geometry: stage?.geometry?.length ? JSON.stringify(stage?.geometry) : undefined,

                                parcels: parcels.map(v => v?.uuid || '') || [],

                                foreignDeviceLength: toNumber(stage?.foreignDeviceLength) || undefined,
                                foreignDeviceWidth: toNumber(stage?.foreignDeviceWidth) || undefined,
                                foreignDeviceArea: toNumber(stage?.foreignDeviceArea) || undefined,
                                roadOccupation: roadOccupation?.uuid
                              }
                            })
                          )
                        );
                      })
                    )
                  )
                ))
              );
          }));
      }),
      switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
    );
  }

  public updateAdPlacementOccupations(application: ApplicationAppEntityApplicationRead | null, occupations: AdPlacementRoadOccupationFormValue[]): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = (application?.roadOccupations || []).map((v: any) => this.roadOccupationService.deleteRoadOccupationRoadOccupationItem({ id: v?.uuid || '' }));
    application?.roadOccupations?.forEach(occupation => {
      occupation?.locationDecisions?.forEach(v => {
        deleteRequests.push(this.locationDecisionService.deleteLocationDecisionLocationDecisionItem({
          id: v?.uuid || ''
        }));
      });

      occupation?.stages?.forEach(stage => {
        stage?.parcels?.forEach(parcel => {
          deleteRequests.push(this.parcelService.deleteParcelParcelItem({ id: parcel?.uuid || '' }));
        });
      });
    });
    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() => forkJoin(
          [...occupations].reverse().map((occupationFormValue: AdPlacementRoadOccupationFormValue) => this.roadOccupationService.postRoadOccupationRoadOccupationCollection({
              roadOccupation: {
                application: application?.uuid
              }
            }).pipe(
              switchMap((occupation: RoadOccupationAppEntityRoadOccupationRead) =>
                (occupationFormValue.locationDecisions.length ? forkJoin(occupationFormValue.locationDecisions.map(locationDecision =>
                    this.locationDecisionService.postLocationDecisionLocationDecisionCollection({
                      locationDecision: {
                        ...locationDecision,
                        roadOccupation: occupation.uuid
                      }
                    })
                  )
                ) : of([])).pipe(
                  switchMap(() => forkJoin(
                    [...occupationFormValue.stages].reverse().map((stage, index) => {
                      const createParcelsRequests = stage.parcels.map(parcel => this.parcelService.postParcelParcelCollection({ parcel }));
                      return (createParcelsRequests.length ? forkJoin(createParcelsRequests) : of([])).pipe(
                        switchMap((parcels) => this.roadOccupationStageService.postRoadOccupationStageRoadOccupationStageCollection({
                            roadOccupationStage: {
                              ordinalNumber: index + 1,
                              startDate: stage?.startDate || undefined,
                              endDate: stage?.endDate || undefined,
                              dayQuantity: stage.dayQuantity || 0,

                              adKind: stage.adKind || undefined,
                              adLightingType: stage.adLightingType || undefined,
                              adType: stage.adType || undefined,
                              adWidth: toNumber(stage.adWidth),
                              adLength: toNumber(stage.adLength),
                              adQuantity: toNumber(stage.adQuantity || undefined),
                              adArea: toNumber(stage.adArea),
                              adMountingStructure: stage.adMountingStructure,

                              geometry: stage?.geometry?.length ? JSON.stringify(stage?.geometry) : undefined,

                              roadName: stage.roadName,
                              roadNumber: stage.roadNumber,
                              street: stage.street || '',

                              parcels: parcels.map(v => v?.uuid || '') || [],
                              roadOccupation: occupation?.uuid
                            }
                          })
                        )
                      );
                    })
                  ))
                )
              )
            )
          )
        )
      ),
      switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
    );
  }

  public updateLocationOfDevicesUnrelatedToRoadOccupations(application: ApplicationAppEntityApplicationRead | null, occupations: LocationOfDevicesUnrelatedToRoadOccupationFormValue[]): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = (application?.roadOccupations || []).map((v: any) => this.roadOccupationService.deleteRoadOccupationRoadOccupationItem({ id: v?.uuid || '' }));
    application?.roadOccupations?.forEach(occupation => {
      occupation?.locationDecisions?.forEach(v => {
        deleteRequests.push(this.locationDecisionService.deleteLocationDecisionLocationDecisionItem({
          id: v?.uuid || ''
        }));
      });

      occupation?.stages?.forEach(stage => {
        stage?.parcels?.forEach(parcel => {
          deleteRequests.push(this.parcelService.deleteParcelParcelItem({ id: parcel?.uuid || '' }));
        });
      });
    });
    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() => forkJoin(
          [...occupations].reverse().map(occupationFormValue => {
            const createLocationDecisions = occupationFormValue.locationDecisions.map(locationDecision => this.locationDecisionService.postLocationDecisionLocationDecisionCollection({
              locationDecision
            }).pipe(map(decision => decision?.id || '')));
            return forkJoin(createLocationDecisions).pipe(
              switchMap((locationDecisions) => {
                return this.roadOccupationService.postRoadOccupationRoadOccupationCollection({
                  roadOccupation: {
                    application: application?.uuid,
                    deviceType: occupationFormValue.deviceType,
                    locationDecisions
                  }
                }).pipe(
                  switchMap((occupation) => forkJoin(
                    [...occupationFormValue.stages].reverse().map((stage, index) => {
                      const createParcelsRequests = stage.parcels.map(parcel => this.parcelService.postParcelParcelCollection({ parcel }));
                      return (createParcelsRequests.length ? forkJoin(createParcelsRequests) : of([])).pipe(
                        switchMap((parcels) => this.roadOccupationStageService.postRoadOccupationStageRoadOccupationStageCollection({
                            roadOccupationStage: {
                              startDate: stage?.startDate || undefined,
                              endDate: stage?.endDate || undefined,
                              dayQuantity: stage.dayQuantity || 0,

                              nextYearsFrom: +stage.nextYearsFrom || undefined,
                              nextYearsTo: +stage.nextYearsTo || undefined,
                              yearsCount: +stage.yearsCount || undefined,

                              infrastructureTelecommunicationOnly: stage.infrastructureTelecommunicationOnly,

                              geometry: stage?.geometry?.length ? JSON.stringify(stage?.geometry) : undefined,

                              roadName: stage.roadName,
                              roadNumber: stage.roadNumber,
                              street: stage.street || '',

                              parcels: parcels.map(v => v?.uuid || '') || [],

                              bridgeStructureWidth: toNumber(stage.bridgeStructureWidth),
                              bridgeStructureLength: toNumber(stage.bridgeStructureLength),
                              bridgeStructureArea: toNumber(stage.bridgeStructureArea),

                              outsideBridgeStructureWidth: toNumber(stage.outsideBridgeStructureWidth),
                              outsideBridgeStructureLength: toNumber(stage.outsideBridgeStructureLength),
                              outsideBridgeStructureArea: toNumber(stage.outsideBridgeStructureArea),

                              infrastructureEquipmentWidth: toNumber(stage.infrastructureEquipmentWidth),
                              infrastructureEquipmentLength: toNumber(stage.infrastructureEquipmentLength),
                              infrastructureEquipmentArea: toNumber(stage.infrastructureEquipmentArea),

                              ordinalNumber: index + 1,
                              roadOccupation: occupation?.uuid
                            }
                          })
                        )
                      );
                    })
                  ))
                );
              })
            );
          })
        )
      ),
      switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
    );
  }
}
